import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { useProvider } from 'src/modules/providers/hooks/useProvider';
import { ConfirmModal, ConfirmModalProps } from 'src/modules/care/components/ConfirmModal';
import { toastNotificationsService } from 'src/config/rootService';

interface Props {
    isVisible: ConfirmModalProps['isVisible'];
    onCancel: ConfirmModalProps['onCancel'];
    onConfirm: ConfirmModalProps['onConfirm'];
    providerUuid: string;
}

export const RemoveProviderModal: React.FC<Props> = ({
    isVisible,
    onCancel,
    onConfirm,
    providerUuid,
}) => {
    const { t } = useTranslation();
    const { removeProvider, fetchMyProviders } = useProvider();

    const providers = useSelector(getMyProviders);
    const provider = useMemo(
        () => providers.find(p => p.uuid === providerUuid),
        [providerUuid, providers],
    );

    // If there is no provider found and the modal is visible,
    // fetch all providers to attempt to update provider data
    // and find a matching one.
    useEffect(() => {
        if (!provider && isVisible) {
            fetchMyProviders();
        }
    }, [isVisible, provider, fetchMyProviders]);

    if (!provider) {
        return null;
    }

    return (
        <ConfirmModal
            type="remove"
            isVisible={isVisible}
            onConfirm={async () => {
                await removeProvider(provider.uuid);
                toastNotificationsService.renderSuccessToast(
                    t('providers:remove_provider_success', {
                        name: provider.firstName,
                    }),
                );
                await fetchMyProviders();
                onConfirm();
            }}
            onCancel={onCancel}
            title={t('providers:provider_actions.remove.title', {
                name: provider.firstName,
            })}
            description={t('providers:provider_actions.remove.description', {
                name: provider.firstName,
            })}
            confirmLabel={t('providers:provider_actions.remove.button_text', {
                name: provider.firstName,
            })}
        />
    );
};
